import { useEffect, useState } from 'react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AuthButton } from '../auth/auth-button';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/auth-context';
import { BookOpen, ChevronRight, AlertCircle } from 'lucide-react';
import { W7SBuildBrand } from '../icons/logos';
import { usePlatformStatus } from '@/hooks/use-platform-status';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Link, useLocation } from 'react-router';
import clsx from 'clsx';
import { CreditBalanceIndicator } from '@/components/billing/credit-balance-indicator';

export function GlobalHeader() {
	const { user } = useAuth();
	const { status } = usePlatformStatus();
	const [isChangelogOpen, setIsChangelogOpen] = useState(false);
	const hasMaintenanceMessage = Boolean(
		status.hasActiveMessage && status.globalUserMessage.trim().length > 0,
	);
	const hasChangeLogs = Boolean(
		status.changeLogs && status.changeLogs.trim().length > 0,
	);
	const { pathname } = useLocation();

	useEffect(() => {
		if (!hasChangeLogs) {
			setIsChangelogOpen(false);
		}
	}, [hasChangeLogs]);

	return (
		<Dialog open={isChangelogOpen} onOpenChange={setIsChangelogOpen}>
			<motion.header
				initial={{ y: -10, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				transition={{ duration: 0.2, ease: 'easeOut' }}
				className={clsx(
					'sticky top-0 z-50 border-b border-white/10 bg-[#050505]/90 backdrop-blur-md',
					pathname === '/' && 'bg-[#050505]/80',
				)}
			>
				<div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-10">
					<div className="relative z-10 grid grid-cols-[auto_1fr_auto] items-center gap-4 py-3">
						{/* Left section */}
						<motion.div
							whileTap={{ scale: 0.98 }}
							transition={{
								type: 'spring',
								stiffness: 400,
								damping: 17,
							}}
							className="flex items-center"
						>
							{user && (
								<SidebarTrigger className="h-8 w-8 text-text-primary hover:bg-accent/10 transition-colors duration-200" />
							)}
							<Link
								to="/"
								className={clsx(
									'inline-flex items-center px-1.5 py-1 transition-colors hover:text-accent',
									user ? 'ml-2' : 'ml-0',
								)}
								aria-label="Go to home"
							>
								<W7SBuildBrand />
							</Link>
							{user && hasMaintenanceMessage && (
								<button
									type="button"
									onClick={
										hasChangeLogs
											? () => setIsChangelogOpen(true)
											: undefined
									}
									disabled={!hasChangeLogs}
									className={`flex max-w-full items-center gap-2 border border-accent/35 bg-bg-4/85 px-3 ml-3 py-1.5 text-xs text-text-primary shadow-sm backdrop-blur transition-colors hover:bg-accent/10 focus:outline-none focus:ring-2 focus:ring-accent/40 md:text-sm${!hasChangeLogs ? ' opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
									aria-label="Platform updates"
								>
									<AlertCircle className="h-4 w-4 text-accent" />
									<span className="truncate max-w-[46ch] md:max-w-[60ch]">
										{status.globalUserMessage}
									</span>
									<ChevronRight className="ml-1 h-4 w-4 text-accent" />
								</button>
							)}
						</motion.div>

						{/* Right section */}
						<motion.div
							initial={{ opacity: 0, x: 10 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.2 }}
							className="flex flex-wrap items-center justify-end gap-3 justify-self-end"
						>
							{/* Disable cost display for now */}
							{/* {user && (
							<CostDisplay
								{...extractUserAnalyticsProps(analytics)}
								loading={analyticsLoading}
								variant="inline"
							/>
						)} */}
							<a
								href="https://www.w7s.io/docs/"
								target="_blank"
								rel="noopener noreferrer"
								className="hidden items-center gap-2 text-xs font-bold uppercase text-text-tertiary transition-colors hover:text-accent sm:inline-flex"
							>
								<BookOpen
									className="h-3.5 w-3.5"
									strokeWidth={1.5}
								/>
								Docs
							</a>
							<CreditBalanceIndicator />
							<AuthButton />
						</motion.div>
					</div>
				</div>
			</motion.header>
			{hasChangeLogs && (
				<DialogContent className="max-w-xl">
					<DialogHeader>
						<DialogTitle>Platform updates</DialogTitle>
						{status.globalUserMessage && (
							<DialogDescription className="text-sm text-muted-foreground">
								{status.globalUserMessage}
							</DialogDescription>
						)}
					</DialogHeader>
					<ScrollArea className="max-h-[60vh] pr-4">
						<p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
							{status.changeLogs}
						</p>
					</ScrollArea>
				</DialogContent>
			)}
		</Dialog>
	);
}
